import React, { useState, useEffect } from 'react';

const FilterPresets = ({ 
  currentFilters, 
  onLoadPreset, 
  onSavePreset,
  userTier = 'free'
}) => {
  const [presets, setPresets] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState(null);

  // Load presets from localStorage on mount
  useEffect(() => {
    const savedPresets = localStorage.getItem('leadsFilterPresets');
    if (savedPresets) {
      try {
        setPresets(JSON.parse(savedPresets));
      } catch (error) {
        console.error('Error loading filter presets:', error);
      }
    }
  }, []);

  // Save presets to localStorage when they change
  useEffect(() => {
    localStorage.setItem('leadsFilterPresets', JSON.stringify(presets));
  }, [presets]);

  const handleSavePreset = () => {
    if (!presetName.trim()) {
      alert('Please enter a preset name');
      return;
    }

    const newPreset = {
      id: Date.now(),
      name: presetName.trim(),
      filters: currentFilters,
      createdAt: new Date().toISOString(),
      userTier
    };

    setPresets(prev => [...prev, newPreset]);
    setPresetName('');
    setShowSaveModal(false);
  };

  const handleLoadPreset = (preset) => {
    onLoadPreset(preset.filters);
    setSelectedPreset(preset.id);
  };

  const handleDeletePreset = (presetId) => {
    setPresetToDelete(presetId);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePreset = () => {
    if (presetToDelete) {
      setPresets(prev => prev.filter(p => p.id !== presetToDelete));
      if (selectedPreset === presetToDelete) {
        setSelectedPreset(null);
      }
    }
    setShowDeleteConfirm(false);
    setPresetToDelete(null);
  };

  const cancelDeletePreset = () => {
    setShowDeleteConfirm(false);
    setPresetToDelete(null);
  };

  const getPresetCount = () => {
    switch (userTier) {
      case 'free':
        return 3;
      case 'starter':
        return 10;
      case 'pro':
        return 50;
      default:
        return 3;
    }
  };

  const canSavePreset = presets.length < getPresetCount();

  const styles = {
    container: {
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '12px',
      padding: '16px',
      backdropFilter: 'blur(10px)',
      marginBottom: '16px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px'
    },
    title: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#ffffff',
      margin: 0
    },
    saveButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 12px',
      background: canSavePreset ? 'rgba(59, 130, 246, 0.2)' : 'rgba(107, 114, 128, 0.2)',
      border: `1px solid ${canSavePreset ? 'rgba(59, 130, 246, 0.3)' : 'rgba(107, 114, 128, 0.3)'}`,
      borderRadius: '6px',
      color: canSavePreset ? '#3B82F6' : '#6B7280',
      fontSize: '12px',
      fontWeight: '500',
      cursor: canSavePreset ? 'pointer' : 'not-allowed',
      transition: 'all 0.2s ease',
      '&:hover': canSavePreset ? {
        background: 'rgba(59, 130, 246, 0.3)',
        transform: 'translateY(-1px)'
      } : {}
    },
    presetsList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px'
    },
    presetItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 12px',
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '16px',
      color: '#ffffff',
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.15)',
        transform: 'translateY(-1px)'
      }
    },
    presetItemActive: {
      background: 'rgba(59, 130, 246, 0.2)',
      borderColor: 'rgba(59, 130, 246, 0.3)',
      color: '#3B82F6'
    },
    deleteButton: {
      background: 'none',
      border: 'none',
      color: 'rgba(255, 255, 255, 0.5)',
      cursor: 'pointer',
      fontSize: '12px',
      padding: '2px',
      borderRadius: '50%',
      width: '16px',
      height: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'rgba(239, 68, 68, 0.2)',
        color: '#EF4444'
      }
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      animation: 'fadeIn 0.3s ease forwards'
    },
    modalContent: {
      background: 'rgba(30, 30, 30, 0.95)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      padding: '24px',
      width: '90%',
      maxWidth: '400px',
      backdropFilter: 'blur(10px)',
      animation: 'slideInUp 0.3s ease forwards'
    },
    modalTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#ffffff',
      marginBottom: '16px'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '14px',
      outline: 'none',
      marginBottom: '16px',
      transition: 'all 0.2s ease',
      '&:focus': {
        borderColor: '#3B82F6',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
      }
    },
    modalActions: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end'
    },
    modalButton: {
      padding: '8px 16px',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: 'none'
    },
    cancelButton: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: '#ffffff',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.15)'
      }
    },
    confirmButton: {
      background: '#3B82F6',
      color: '#ffffff',
      '&:hover': {
        background: '#2563EB'
      }
    },
    limitInfo: {
      fontSize: '11px',
      color: 'rgba(255, 255, 255, 0.6)',
      marginTop: '8px'
    }
  };

  return (
    <>
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.title}>Filter Presets</h3>
          <button
            style={styles.saveButton}
            onClick={() => setShowSaveModal(true)}
            disabled={!canSavePreset}
            title={canSavePreset ? 'Save current filters as preset' : `Limit reached (${getPresetCount()} presets)`}
          >
            💾 Save Preset
          </button>
        </div>

        {presets.length > 0 ? (
          <div style={styles.presetsList}>
            {presets.map(preset => (
              <div
                key={preset.id}
                style={{
                  ...styles.presetItem,
                  ...(selectedPreset === preset.id ? styles.presetItemActive : {})
                }}
                onClick={() => handleLoadPreset(preset)}
                title={`Load "${preset.name}" preset`}
              >
                <span>{preset.name}</span>
                <button
                  style={styles.deleteButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePreset(preset.id);
                  }}
                  title="Delete preset"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
            No saved presets. Save your current filters to create a preset.
          </div>
        )}

        <div style={styles.limitInfo}>
          {presets.length}/{getPresetCount()} presets used
        </div>
      </div>

      {/* Save Preset Modal */}
      {showSaveModal && (
        <div style={styles.modal} onClick={() => setShowSaveModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Save Filter Preset</h3>
            <input
              type="text"
              placeholder="Enter preset name..."
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              style={styles.input}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSavePreset();
                } else if (e.key === 'Escape') {
                  setShowSaveModal(false);
                }
              }}
            />
            <div style={styles.modalActions}>
              <button
                style={{ ...styles.modalButton, ...styles.cancelButton }}
                onClick={() => setShowSaveModal(false)}
              >
                Cancel
              </button>
              <button
                style={{ ...styles.modalButton, ...styles.confirmButton }}
                onClick={handleSavePreset}
                disabled={!presetName.trim()}
              >
                Save Preset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={styles.modal} onClick={cancelDeletePreset}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Delete Preset</h3>
            <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '16px' }}>
              Are you sure you want to delete this preset? This action cannot be undone.
            </p>
            <div style={styles.modalActions}>
              <button
                style={{ ...styles.modalButton, ...styles.cancelButton }}
                onClick={cancelDeletePreset}
              >
                Cancel
              </button>
              <button
                style={{ ...styles.modalButton, ...styles.confirmButton }}
                onClick={confirmDeletePreset}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default FilterPresets; 